import { useMemo, type ComponentProps } from 'react';
import ReactMarkdown from 'react-markdown';
import { AlertCard } from './AlertCard';
import { preprocessTickers, navigateToTicker } from '../../utils/tickerLinks';
import type { FeedMessage as FeedMessageType } from '../../services/wsjFeedService';

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

interface FeedMessageProps {
  message: FeedMessageType;
  onImageClick?: (images: { url: string; filename: string }[], index: number) => void;
  onTickerClick?: (symbol: string) => void;
  showEdited?: boolean;
}

// Custom link renderer: intercept /ticker/ links, open others normally
const markdownComponents: ComponentProps<typeof ReactMarkdown>['components'] = {
  a: ({ href, children }) => {
    if (href?.startsWith('/ticker/')) {
      const symbol = href.replace('/ticker/', '');
      return (
        <button
          onClick={() => navigateToTicker(symbol)}
          className="text-accent-yellow font-medium hover:text-wheat hover:underline cursor-pointer"
        >
          {children}
        </button>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

export function FeedMessage({ message, onImageClick, onTickerClick, showEdited = true }: FeedMessageProps): React.JSX.Element {
  const msg = message;
  const isEdited = showEdited && Math.abs(new Date(msg.updated_at).getTime() - new Date(msg.created_at).getTime()) > 1000;

  // Preprocess $TICKER mentions into markdown links for composer messages
  const processedContent = useMemo(() => {
    if (msg.source_type === 'wiseguy_alert') return msg.content;
    return preprocessTickers(msg.content);
  }, [msg.content, msg.source_type]);

  const imageAttachments = msg.attachments?.filter(att => att.content_type?.startsWith('image/')) || [];
  const fileAttachments = msg.attachments?.filter(att => !att.content_type?.startsWith('image/')) || [];

  return (
    <div className="group">
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-xs text-gray-500">
          {formatTime(msg.created_at)}
        </span>
        {isEdited && (
          <span className="text-xs text-gray-600">(edited)</span>
        )}
      </div>
      {msg.source_type === 'wiseguy_alert' && msg.metadata?.symbol ? (
        <AlertCard metadata={msg.metadata} comment={msg.content} onTickerClick={onTickerClick} />
      ) : (
        <div className="text-sm text-white break-words prose prose-invert prose-sm max-w-none prose-p:my-1 prose-headings:my-1 prose-headings:text-white prose-a:text-accent-yellow prose-strong:text-white prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
          <ReactMarkdown components={markdownComponents}>{processedContent}</ReactMarkdown>
        </div>
      )}
      {(imageAttachments.length > 0 || fileAttachments.length > 0) && (
        <div className="mt-1 flex flex-wrap gap-2">
          {imageAttachments.map((att, i) => (
            <button
              key={i}
              onClick={() => onImageClick?.(imageAttachments.map(a => ({ url: a.url, filename: a.filename })), i)}
              className="block"
            >
              <img
                src={att.url}
                alt={att.filename}
                className="max-w-xs max-h-48 rounded border border-dark-border hover:border-accent-yellow transition-colors cursor-pointer"
              />
            </button>
          ))}
          {fileAttachments.map((att, i) => (
            <a
              key={i}
              href={att.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-accent-yellow bg-dark-bg rounded border border-dark-border hover:border-accent-yellow transition-colors"
            >
              <span>📎</span>
              <span>{att.filename}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
